/**
 * Stretch Engine — Core State Machine
 * Pure state machine: createStretchState(config) → state, stepStretch(state, input) → state
 * No DB, no server, no side effects.
 */

import {
  createFogGrid,
  createLightGrid,
  computeVisibility,
  updateFog,
} from '@omen-quest/grid-map';

import type {
  StretchState,
  StretchConfig,
  StretchInput,
  StretchPlayer,
} from './types';
import {
  DEFAULT_MOVEMENT_BUDGET,
  DEFAULT_VISION_RADIUS,
  DEFAULT_PASSIVE_PERCEPTION,
} from './types';
import { validateCommit } from './validation';
import { resolveCommits } from './activities';
import { evaluateEvents } from './events';
import { computePlayerVision } from './crawl-strategy';

// ============================================================================
// CREATE
// ============================================================================

/** Create the initial stretch state from a config. */
export function createStretchState(config: StretchConfig): StretchState {
  const { map } = config;
  const openDoors = config.openDoors ? new Set(config.openDoors) : new Set<string>();

  const players: StretchPlayer[] = config.players.map(p => {
    const visionRadius = p.visionRadius ?? DEFAULT_VISION_RADIUS;
    const fogGrid = createFogGrid(map.width, map.height);
    const lightGrid = createLightGrid(map.width, map.height);

    // Compute initial visibility for this player
    const effectiveRadius = computePlayerVision(
      visionRadius,
      config.crawlStrategy?.lightSources ?? [],
      p.id,
    );
    if (effectiveRadius > 0) {
      const vis = computeVisibility(map, p.x, p.y, effectiveRadius, openDoors);
      updateFog(fogGrid, lightGrid, vis);
    }

    return {
      id: p.id,
      name: p.name,
      x: p.x,
      y: p.y,
      movementBudget: p.movementBudget ?? DEFAULT_MOVEMENT_BUDGET,
      visionRadius,
      passivePerception: p.passivePerception ?? DEFAULT_PASSIVE_PERCEPTION,
      fogGrid,
      lightGrid,
      hasCommitted: false,
      isKeepingWatch: false,
      isStealthed: false,
    };
  });

  return {
    phase: 'planning',
    stretchNumber: 1,
    map,
    openDoors,
    firedTriggers: config.firedTriggers ? new Set(config.firedTriggers) : new Set<string>(),
    triggerFlags: config.triggerFlags ? new Set(config.triggerFlags) : new Set<string>(),
    crawlStrategy: {
      lightSources: config.crawlStrategy?.lightSources ?? [],
      stealthMode: config.crawlStrategy?.stealthMode ?? false,
    },
    players,
    commits: [],
    wanderingMonsters: {
      frequency: config.wanderingMonsters?.frequency ?? 'medium',
      likelihood: config.wanderingMonsters?.likelihood ?? 'possible',
      encounterTable: config.wanderingMonsters?.encounterTable ?? [],
      stretchesSinceLastCheck: config.wanderingMonsters?.stretchesSinceLastCheck ?? 0,
    },
    timeline: config.timeline ?? [],
    countdowns: config.countdowns ?? [],
    lastResolution: null,
    outcome: 'active',
  };
}

// ============================================================================
// STEP
// ============================================================================

/**
 * Advance the stretch state machine.
 * - commit input: record a player's activity choice (planning phase)
 * - step input: advance to the next phase (resolving → events → planning)
 */
export function stepStretch(state: StretchState, input: StretchInput): StretchState {
  if (state.phase === 'ended') return state;

  switch (input.type) {
    case 'commit':
      return handleCommit(state, input.playerId, input.activity);
    case 'step':
      return handleStep(state);
    default:
      return state;
  }
}

// ============================================================================
// COMMIT HANDLING
// ============================================================================

import type { StretchActivity } from './types';

function handleCommit(
  state: StretchState,
  playerId: string,
  activity: StretchActivity,
): StretchState {
  if (state.phase !== 'planning') return state;

  const playerIdx = state.players.findIndex(p => p.id === playerId);
  if (playerIdx === -1) return state;
  if (state.players[playerIdx].hasCommitted) return state;

  // Validate the commit
  const error = validateCommit(state, playerId, activity);
  if (error) return state;

  // Apply commit
  const next = structuredClone(state) as StretchState;
  // structuredClone converts Sets to empty objects — restore them
  next.openDoors = new Set(state.openDoors);
  next.firedTriggers = new Set(state.firedTriggers);
  next.triggerFlags = new Set(state.triggerFlags);

  next.players[playerIdx].hasCommitted = true;
  next.commits.push({ playerId, activity });

  // If all players committed, auto-transition to resolving
  if (next.players.every(p => p.hasCommitted)) {
    next.phase = 'resolving';
  }

  return next;
}

// ============================================================================
// PHASE TRANSITIONS
// ============================================================================

function handleStep(state: StretchState): StretchState {
  switch (state.phase) {
    case 'resolving':
      return resolvePhase(state);
    case 'events':
      return eventsPhase(state);
    default:
      return state;
  }
}

/** Resolve all committed activities and transition to events phase. */
function resolvePhase(state: StretchState): StretchState {
  const resolved = resolveCommits(state);
  resolved.phase = 'events';
  return resolved;
}

/** Evaluate timeline events, wandering monsters, countdowns. Advance to next stretch or end. */
function eventsPhase(state: StretchState): StretchState {
  const afterEvents = evaluateEvents(state);

  // Check if combat was triggered
  if (afterEvents.outcome === 'combat_triggered') {
    afterEvents.phase = 'ended';
    return afterEvents;
  }

  // Check if any other end condition
  if (afterEvents.outcome !== 'active') {
    afterEvents.phase = 'ended';
    return afterEvents;
  }

  // Advance to next stretch
  afterEvents.phase = 'planning';
  afterEvents.stretchNumber += 1;
  afterEvents.commits = [];
  for (const player of afterEvents.players) {
    player.hasCommitted = false;
    player.isKeepingWatch = false;
    player.isStealthed = false;
  }

  return afterEvents;
}
